import { useEffect, useState, useRef } from "react";
import { NavLink, Routes, Route, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { BRAND_LOGO, formatMoney, shortDate, discountLabel } from "../lib/brand";
import { Power, House, Storefront, Users, Receipt, Calendar, Megaphone, ChartLine, Warning, Tag, Coins, IdentificationBadge, CreditCard, QrCode, Drop, ShoppingBag, Package, Buildings } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { toast } from "sonner";
import { ImageUpload, GalleryUpload } from "../components/brand/ImageUpload";
import CardActions from "../components/brand/CardActions";
import { QRCodeSVG } from "qrcode.react";
import { useBrandLogo } from "../lib/brandAssets";

const Sidebar = ({ logout, name }) => (
  <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/5 bg-[#070707]">
    <div className="p-5 flex items-center gap-3 border-b border-white/5">
      <img src={BRAND_LOGO} alt="" className="h-10 w-10 rounded-full ring-1 ring-[#D4AF37]/40" />
      <div>
        <p className="font-display text-lg text-white leading-tight">Super Admin</p>
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">The Circle</p>
      </div>
    </div>
    <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
      {[
        { to: "/admin", icon: <House size={18} />, label: "Overview", end: true },
        { to: "/admin/merchants", icon: <Storefront size={18} />, label: "Merchants" },
        { to: "/admin/merchant-applications", icon: <Buildings size={18} />, label: "Merchant Applications" },
        { to: "/admin/categories", icon: <Tag size={18} />, label: "Categories" },
        { to: "/admin/members", icon: <Users size={18} />, label: "Members" },
        { to: "/admin/membership-requests", icon: <IdentificationBadge size={18} />, label: "Membership Requests" },
        { to: "/admin/hydro-applications", icon: <Drop size={18} />, label: "Hydro Applications" },
        { to: "/admin/shop", icon: <ShoppingBag size={18} />, label: "Shop · Products" },
        { to: "/admin/orders", icon: <Package size={18} />, label: "Shop · Orders" },
        { to: "/admin/payment-settings", icon: <CreditCard size={18} />, label: "Payment Settings" },
        { to: "/admin/qr-codes", icon: <QrCode size={18} />, label: "Elegant QR Codes" },
        { to: "/admin/transactions", icon: <Receipt size={18} />, label: "Transactions" },
        { to: "/admin/settlements", icon: <Coins size={18} />, label: "Settlements" },
        { to: "/admin/events", icon: <Calendar size={18} />, label: "Events" },
        { to: "/admin/popup-ads", icon: <Megaphone size={18} />, label: "Pop-up Ads" },
        { to: "/admin/analytics", icon: <ChartLine size={18} />, label: "Analytics" },
        { to: "/admin/suspicious", icon: <Warning size={18} />, label: "Suspicious" },
      ].map((l) => (
        <NavLink key={l.to} to={l.to} end={l.end}
          className={({isActive}) => `flex items-center gap-2 px-4 py-2.5 rounded-md text-[11px] tracking-luxe uppercase whitespace-nowrap ${isActive ? "bg-[#0d0d0d] text-[#D4AF37] border border-[#D4AF37]/30" : "text-white/65 hover:text-white"}`}
          data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
          {l.icon} {l.label}
        </NavLink>
      ))}
    </nav>
    <div className="p-4 lg:mt-auto border-t border-white/5 hidden lg:flex items-center justify-between">
      <span className="text-[10px] tracking-luxe uppercase text-white/40">{name}</span>
      <button onClick={logout} className="text-white/60 hover:text-white" data-testid="admin-logout"><Power size={18} /></button>
    </div>
  </aside>
);

const Stat = ({ label, value, sub, testid, accent }) => (
  <div className="tsc-card p-5" data-testid={testid}>
    <p className="text-[10px] tracking-luxe uppercase text-white/60">{label}</p>
    <p className={`font-display text-2xl mt-1 ${accent ? "text-[#D4AF37]" : "text-white"}`}>{value}</p>
    {sub && <p className="text-white/40 text-xs mt-1">{sub}</p>}
  </div>
);

function Overview() {
  const [s, setS] = useState(null);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);
  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => setS(data));
    api.get("/admin/analytics/series", { params: { days: 30 } }).then(({ data }) => setSeries(data));
    api.get("/admin/analytics/top-merchants").then(({ data }) => setTop(data));
  }, []);
  if (!s) return <p className="text-white/60">Loading…</p>;
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Overview</p>
      <h1 className="font-display text-4xl text-white mt-2">The Circle, in numbers</h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat label="Active partners" value={s.active_merchants} sub={`of ${s.total_merchants} total`} testid="overview-merchants" />
        <Stat label="Active members" value={s.active_members} sub={`of ${s.total_members} total`} testid="overview-active-members" />
        <Stat label="Pending verification" value={s.pending_verification} sub={`${s.pending_payment} pending payment`} testid="overview-pending" accent />
        <Stat label="Expiring (30d)" value={s.expiring_soon} sub={`${s.rejected_members} rejected · ${s.suspended_members} suspended`} testid="overview-expiring" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Stat label="Transactions" value={s.total_transactions} sub={`${s.total_reservations} reservations`} testid="overview-tx" />
        <Stat label="Suspicious flags" value={s.suspicious_count} testid="overview-suspicious" />
        <Stat label="Customer discounts" value={formatMoney(s.total_customer_discount)} testid="overview-customer-discount" />
        <Stat label="Social Circle receivable" value={formatMoney(s.social_circle_receivable)} sub={`${s.open_settlements} open`} testid="overview-receivable" accent />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4" data-testid="overview-pending-row">
        <Link to="/admin/membership-requests" className="block"><Stat label="Pending memberships" value={s.pending_regular_membership} sub="awaiting review" testid="overview-pending-membership" accent={s.pending_regular_membership > 0} /></Link>
        <Link to="/admin/hydro-applications" className="block"><Stat label="Pending Hydro" value={s.pending_hydro} sub={`${s.active_hydro} active`} testid="overview-pending-hydro" accent={s.pending_hydro > 0} /></Link>
        <Link to="/admin/orders" className="block"><Stat label="Pending orders" value={s.pending_orders} sub="awaiting payment review" testid="overview-pending-orders" accent={s.pending_orders > 0} /></Link>
        <Link to="/admin/merchant-applications" className="block"><Stat label="Pending merchant applications" value={s.pending_merchant_applications} testid="overview-pending-apps" accent={s.pending_merchant_applications > 0} /></Link>
      </div>

      <div className="tsc-card p-6 mt-8">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Last 30 days · Gross / Margin</p>
        <div className="h-72 mt-4">
          <ResponsiveContainer>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="date" stroke="#666" fontSize={10} />
              <YAxis stroke="#666" fontSize={10} />
              <RTooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #333" }} />
              <Line type="monotone" dataKey="gross" stroke="#D4AF37" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="margin" stroke="#F9E596" strokeWidth={1.5} dot={false} />
              <Line type="monotone" dataKey="customer_discount" stroke="#888" strokeWidth={1.2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="tsc-card p-6 mt-8">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Top merchants by gross</p>
        <div className="h-72 mt-4">
          <ResponsiveContainer>
            <BarChart data={top.map(t => ({ name: t.merchant?.business_name || "—", gross: t.gross, margin: t.margin }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="name" stroke="#666" fontSize={10} />
              <YAxis stroke="#666" fontSize={10} />
              <RTooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #333" }} />
              <Bar dataKey="gross" fill="#D4AF37" />
              <Bar dataKey="margin" fill="#888" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}

function MerchantsAdmin() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = { business_name: "", category_id: "", description: "", logo_url: "", cover_url: "", gallery: [],
    merchant_allocation_type: "percentage", merchant_allocation_value: 30,
    member_discount_type: "percentage", member_discount_value: 15,
    promo_text: "", terms_and_conditions: "", featured: false, status: "active",
    owner_email: "", owner_password: "", owner_name: "" };
  const [form, setForm] = useState(blank);

  const load = () => api.get("/merchants", { params: { status: "" } }).then(({ data }) => setList(data));
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const openCreate = () => { setEditId(null); setForm(blank); setOpen(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      ...blank,
      business_name: m.business_name || "",
      category_id: m.category_id || "",
      description: m.description || "",
      logo_url: m.logo_url || "",
      cover_url: m.cover_url || "",
      gallery: m.gallery || [],
      merchant_allocation_type: m.merchant_allocation_type || "percentage",
      merchant_allocation_value: m.merchant_allocation_value ?? 30,
      member_discount_type: m.member_discount_type || "percentage",
      member_discount_value: m.member_discount_value ?? 15,
      promo_text: m.promo_text || "",
      terms_and_conditions: m.terms_and_conditions || "",
      featured: !!m.featured,
      status: m.status || "active",
    });
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const closeForm = () => { setOpen(false); setEditId(null); setForm(blank); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        // PATCH: only send merchant fields (no owner credential fields)
        const { owner_email, owner_password, owner_name, ...patch } = form;
        await api.patch(`/merchants/${editId}`, patch);
        toast.success("Merchant updated");
      } else {
        await api.post("/merchants", form);
        toast.success("Merchant created");
      }
      closeForm();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete merchant "${m.business_name}"?\n\nThis removes the merchant, all branches, and the owner login. This cannot be undone.`)) return;
    try {
      await api.delete(`/merchants/${m.id}`);
      toast.success("Merchant deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resetOwnerPassword = async (m) => {
    const pw = window.prompt(`Set a new owner password for "${m.business_name}".\nMinimum 6 characters.`);
    if (!pw) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      const { data } = await api.post(`/merchants/${m.id}/reset-owner-password`, { password: pw });
      toast.success(`Password reset for ${data.owner_email}`);
    } catch (err) {
      const msg = formatApiError(err);
      if (msg.toLowerCase().includes("not found")) {
        if (window.confirm("No owner login exists yet. Create one now?")) {
          const email = window.prompt("Owner email:");
          if (!email) return;
          const name = window.prompt("Owner name (optional):") || "";
          try {
            await api.post(`/merchants/${m.id}/create-owner`, { email, password: pw, name });
            toast.success("Owner login created");
          } catch (err2) { toast.error(formatApiError(err2)); }
        }
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Partners</p>
          <h1 className="font-display text-4xl text-white mt-2">Merchants</h1>
        </div>
        {open ? (
          <button onClick={closeForm} className="tsc-btn-outline" data-testid="admin-merchant-cancel">Cancel</button>
        ) : (
          <button onClick={openCreate} className="tsc-btn-gold" data-testid="admin-add-merchant">+ New Merchant</button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="tsc-card p-6 mt-6 grid sm:grid-cols-2 gap-4" data-testid="admin-merchant-form">
          <div className="sm:col-span-2 flex items-center justify-between">
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">{editId ? "Edit merchant" : "New merchant"}</p>
            {editId && (
              <span className="text-white/40 text-[10px] tracking-luxe uppercase">ID · {editId.slice(0, 8)}</span>
            )}
          </div>
          <Field label="Business name"><input required value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className="tsc-input" data-testid="merchant-form-name" /></Field>
          <Field label="Category">
            <select required value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} className="tsc-input" data-testid="merchant-form-category">
              <option value="">Select…</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4">
            <div>
              <ImageUpload label="Logo" value={form.logo_url} onChange={(url) => setForm({...form, logo_url: url})} aspect="aspect-square" testId="merchant-form-logo" />
              <p className="text-white/40 text-[10px] mt-1.5 leading-relaxed">512 × 512 px · PNG / WEBP · transparent background · ≤5MB</p>
            </div>
            <div className="sm:col-span-2">
              <ImageUpload label="Cover image" value={form.cover_url} onChange={(url) => setForm({...form, cover_url: url})} aspect="aspect-video" testId="merchant-form-cover" />
              <p className="text-white/40 text-[10px] mt-1.5 leading-relaxed">1600 × 900 px (16:9) · landscape hero shot · ≤5MB</p>
            </div>
          </div>
          <div className="sm:col-span-2">
            <GalleryUpload value={form.gallery} onChange={(arr) => setForm({...form, gallery: arr})} testId="merchant-form-gallery" />
            <p className="text-white/40 text-[10px] mt-1.5 leading-relaxed">Up to 12 photos · 1200 × 1200 px or larger · ≤5MB each</p>
          </div>
          <Field label="Description" full><textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="tsc-input" rows="3" /></Field>
          <Field label="Promo text" full><input value={form.promo_text} onChange={(e) => setForm({...form, promo_text: e.target.value})} className="tsc-input" placeholder="Live acoustic nights · curated cocktails" /></Field>
          <Field label="Merchant Terms & Conditions" full>
            <textarea value={form.terms_and_conditions} onChange={(e) => setForm({...form, terms_and_conditions: e.target.value})} className="tsc-input" rows="5" placeholder="e.g. Discount applies on food only. Not valid on alcoholic beverages. Not combinable with other promos. Blackout dates: Dec 24, Dec 31. Minimum spend ₱500. Subject to merchant validation." data-testid="merchant-form-terms" />
          </Field>

          <div className="sm:col-span-2 border-t border-white/5 mt-2 pt-4">
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Discount & Commission</p>
            <p className="text-white/40 text-xs mt-1">Configure type (Percentage / Fixed) and value for each.</p>
          </div>

          <Field label="Merchant allocation · type">
            <select value={form.merchant_allocation_type} onChange={(e) => setForm({...form, merchant_allocation_type: e.target.value})} className="tsc-input" data-testid="merchant-form-alloc-type">
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed (₱)</option>
            </select>
          </Field>
          <Field label={`Merchant allocation · ${form.merchant_allocation_type === "percentage" ? "%" : "₱"}`}>
            <input type="number" min="0" max={form.merchant_allocation_type === "percentage" ? 100 : undefined} step="0.1" value={form.merchant_allocation_value} onChange={(e) => setForm({...form, merchant_allocation_value: Number(e.target.value)})} className="tsc-input" data-testid="merchant-form-alloc-value" />
          </Field>

          <Field label="Member discount · type">
            <select value={form.member_discount_type} onChange={(e) => setForm({...form, member_discount_type: e.target.value})} className="tsc-input" data-testid="merchant-form-member-type">
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed (₱)</option>
            </select>
          </Field>
          <Field label={`Member discount · ${form.member_discount_type === "percentage" ? "%" : "₱"}`}>
            <input type="number" min="0" max={form.member_discount_type === "percentage" ? 100 : undefined} step="0.1" value={form.member_discount_value} onChange={(e) => setForm({...form, member_discount_value: Number(e.target.value)})} className="tsc-input" data-testid="merchant-form-member-value" />
          </Field>

          <Field label="Featured?"><label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} className="accent-[#D4AF37]" data-testid="merchant-form-featured" /><span className="text-white/70 text-sm">Show on landing</span></label></Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="tsc-input" data-testid="merchant-form-status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </Field>
          {!editId && (
            <div className="sm:col-span-2 border-t border-white/5 mt-2 pt-4">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Optional · Owner login</p>
              <div className="grid sm:grid-cols-3 gap-3 mt-3">
                <input value={form.owner_name} onChange={(e) => setForm({...form, owner_name: e.target.value})} className="tsc-input" placeholder="Owner name" data-testid="merchant-form-owner-name" />
                <input value={form.owner_email} onChange={(e) => setForm({...form, owner_email: e.target.value})} type="email" className="tsc-input" placeholder="Owner email" data-testid="merchant-form-owner-email" />
                <input value={form.owner_password} onChange={(e) => setForm({...form, owner_password: e.target.value})} type="password" className="tsc-input" placeholder="Password (min 6)" data-testid="merchant-form-owner-password" />
              </div>
            </div>
          )}
          <div className="sm:col-span-2 flex items-center gap-3">
            <button type="submit" className="tsc-btn-gold" data-testid="merchant-form-submit">{editId ? "Save Changes" : "Create Merchant"}</button>
            <button type="button" onClick={closeForm} className="text-white/55 text-[10px] tracking-luxe uppercase hover:text-white" data-testid="merchant-form-cancel">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
        {list.map((m) => (
          <div key={m.id} className="tsc-card p-5" data-testid={`admin-merchant-${m.id}`}>
            <div className="flex items-start gap-3">
              {m.logo_url ? <img src={m.logo_url} alt="" className="h-12 w-12 rounded object-cover ring-1 ring-[#D4AF37]/30" /> : <div className="h-12 w-12 rounded bg-[#0d0d0d] border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] font-display">{m.business_name[0]}</div>}
              <div className="flex-1">
                <p className="font-display text-lg text-white leading-tight">{m.business_name}</p>
                <p className="text-white/50 text-xs">{m.category?.name} · {m.status}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-center">
              <Mini label="Member" value={discountLabel(m.member_discount_type, m.member_discount_value)} />
              <Mini label="Alloc" value={discountLabel(m.merchant_allocation_type, m.merchant_allocation_value)} />
            </div>
            <div className="mt-4 flex gap-3 justify-end items-center flex-wrap">
              <Link to={`/partners/${m.id}`} className="text-white/55 hover:text-white text-[10px] tracking-luxe uppercase" data-testid={`admin-merchant-view-${m.id}`}>View</Link>
              <button onClick={() => openEdit(m)} className="text-[#D4AF37] hover:text-[#F9E596] text-[10px] tracking-luxe uppercase" data-testid={`admin-merchant-edit-${m.id}`}>Edit</button>
              <button onClick={() => resetOwnerPassword(m)} className="text-[#D4AF37]/80 hover:text-[#F9E596] text-[10px] tracking-luxe uppercase" data-testid={`admin-merchant-reset-${m.id}`}>Reset PW</button>
              <button onClick={() => del(m)} className="text-white/40 hover:text-red-400 text-[10px] tracking-luxe uppercase" data-testid={`admin-merchant-delete-${m.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-[10px] tracking-luxe uppercase text-white/60">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const Mini = ({ label, value }) => (
  <div className="bg-[#0a0a0a] border border-white/5 rounded px-2 py-1.5">
    <p className="text-[9px] tracking-luxe uppercase text-white/40">{label}</p>
    <p className="text-white text-xs mt-0.5">{value}</p>
  </div>
);

function CategoriesAdmin() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/categories").then(({ data }) => setList(data));
  useEffect(load, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/categories", { name, sort_order: list.length }); setName(""); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Taxonomy</p>
      <h1 className="font-display text-4xl text-white mt-2">Categories</h1>
      <form onSubmit={submit} className="tsc-card p-5 mt-6 flex gap-3" data-testid="admin-cat-form">
        <input value={name} onChange={(e) => setName(e.target.value)} className="tsc-input" placeholder="New category…" required />
        <button className="tsc-btn-gold">Add</button>
      </form>
      <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-6">
        {list.map((c) => (
          <div key={c.id} className="tsc-card p-4 flex items-center justify-between">
            <span className="text-white">{c.name}</span>
            <button onClick={async () => { await api.delete(`/categories/${c.id}`); load(); }} className="text-white/40 hover:text-red-400 text-[10px] tracking-luxe uppercase">×</button>
          </div>
        ))}
      </div>
    </>
  );
}

function MembersAdmin() {
  const [list, setList] = useState([]);
  const load = () => api.get("/members").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const resetPassword = async (m) => {
    const pw = window.prompt(`Set a new password for ${m.full_name} (${m.email}).\nMinimum 6 characters.`);
    if (!pw) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/members/${m.id}/reset-password`, { password: pw });
      toast.success(`Password reset · share it securely with ${m.full_name}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resetIdentityPhoto = async (m) => {
    if (!window.confirm(`Clear identity photo for ${m.full_name}?\nThey'll be prompted to retake it on next login.`)) return;
    try {
      await api.post(`/members/${m.id}/reset-identity-photo`);
      toast.success("Identity photo cleared");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete member "${m.full_name}" (${m.email})?\n\nThis removes their account, QR access, reservations, and history. This cannot be undone.`)) return;
    try {
      await api.delete(`/members/${m.id}`);
      toast.success("Member deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Members</p>
      <h1 className="font-display text-4xl text-white mt-2">The Circle</h1>
      <div className="tsc-card mt-6 divide-y divide-white/5">
        {list.length === 0 ? <p className="text-white/50 italic text-sm p-5">No members yet.</p> : list.map((m) => (
          <div key={m.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm" data-testid={`admin-member-${m.id}`}>
            <div className="min-w-0 flex items-center gap-3">
              {m.identity_photo_url ? (
                <img src={m.identity_photo_url} alt="" className="h-10 w-10 rounded-md object-cover border border-[#D4AF37]/40 flex-shrink-0" data-testid={`admin-member-photo-${m.id}`} />
              ) : (
                <div className="h-10 w-10 rounded-md border border-dashed border-white/15 bg-[#0d0d0d] flex-shrink-0 flex items-center justify-center text-white/30 text-[10px]" title="No identity photo yet" data-testid={`admin-member-no-photo-${m.id}`}>—</div>
              )}
              <div className="min-w-0">
                <p className="text-white truncate">{m.full_name} <span className="text-white/40">· {m.email}</span></p>
                <p className="text-white/40 text-xs">{m.tier_name} · {m.status} · expires {shortDate(m.expiry_date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-white">{m.total_visits} visits</p>
                <p className="text-[#D4AF37] text-xs">Saved {formatMoney(m.total_savings)}</p>
              </div>
              <div className="flex items-center gap-3 border-l border-white/5 pl-4">
                <button onClick={() => resetPassword(m)} className="text-[#D4AF37] hover:text-[#F9E596] text-[10px] tracking-luxe uppercase" data-testid={`admin-member-reset-${m.id}`}>Reset PW</button>
                <button onClick={() => resetIdentityPhoto(m)} className="text-[#D4AF37]/80 hover:text-[#F9E596] text-[10px] tracking-luxe uppercase" data-testid={`admin-member-reset-photo-${m.id}`}>Reset Photo</button>
                <button onClick={() => del(m)} className="text-white/40 hover:text-red-400 text-[10px] tracking-luxe uppercase" data-testid={`admin-member-delete-${m.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function TransactionsAdmin() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/transactions").then(({ data }) => setList(data)); }, []);
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Transactions</p>
      <h1 className="font-display text-4xl text-white mt-2">All transactions</h1>
      <div className="tsc-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-luxe uppercase text-white/40">
            <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Type</th><th className="text-right p-3">Gross</th><th className="text-left p-3">Discount</th><th className="text-right p-3">Customer Pays</th><th className="text-right p-3">Margin</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.map((t) => (
              <tr key={t.id} className="text-white/85" data-testid={`admin-tx-${t.id}`}>
                <td className="p-3 text-white/60 text-xs">{shortDate(t.created_at)}</td>
                <td className="p-3">{t.customer_label}</td>
                <td className="p-3 text-white/60">{t.qr_kind}</td>
                <td className="p-3 text-right">{formatMoney(t.gross_amount)}</td>
                <td className="p-3 text-[#D4AF37] text-xs">
                  −{formatMoney(t.customer_discount)}
                  <span className="block text-white/40">{(t.customer_discount_type === "percentage" || t.customer_discount_type === "percent") ? `${t.customer_discount_value}%` : `₱${t.customer_discount_value} fixed`}</span>
                </td>
                <td className="p-3 text-right">{formatMoney(t.customer_pays)}</td>
                <td className="p-3 text-right text-[#F9E596]">{formatMoney(t.social_circle_margin)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <p className="text-white/50 italic text-sm p-5">No transactions yet.</p>}
      </div>
    </>
  );
}

function SettlementsAdmin() {
  const [list, setList] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [form, setForm] = useState({ merchant_id: "", period_start: "", period_end: "" });
  const load = () => api.get("/settlements").then(({ data }) => setList(data));
  useEffect(() => { load(); api.get("/merchants", { params: { status: "" } }).then(({ data }) => setMerchants(data)); }, []);
  const generate = async (e) => {
    e.preventDefault();
    try {
      const ps = new Date(form.period_start).toISOString();
      const pe = new Date(form.period_end + "T23:59:59").toISOString();
      await api.post("/settlements/generate", { ...form, period_start: ps, period_end: pe });
      load(); toast.success("Settlement generated");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const setStatus = async (sid, status) => {
    await api.patch(`/settlements/${sid}`, { status });
    load();
  };
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Billing</p>
      <h1 className="font-display text-4xl text-white mt-2">Settlements</h1>
      <form onSubmit={generate} className="tsc-card p-5 mt-6 grid sm:grid-cols-4 gap-3" data-testid="admin-settlement-form">
        <select required value={form.merchant_id} onChange={(e) => setForm({...form, merchant_id: e.target.value})} className="tsc-input">
          <option value="">Merchant…</option>
          {merchants.map((m) => <option key={m.id} value={m.id}>{m.business_name}</option>)}
        </select>
        <input required type="date" value={form.period_start} onChange={(e) => setForm({...form, period_start: e.target.value})} className="tsc-input" />
        <input required type="date" value={form.period_end} onChange={(e) => setForm({...form, period_end: e.target.value})} className="tsc-input" />
        <button className="tsc-btn-gold" data-testid="admin-settlement-generate">Generate</button>
      </form>
      <div className="tsc-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-luxe uppercase text-white/40">
            <tr><th className="text-left p-3">Merchant</th><th className="text-left p-3">Period</th><th className="text-right p-3">Tx</th><th className="text-right p-3">Gross</th><th className="text-right p-3">Receivable</th><th className="text-right p-3">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.map((s) => (
              <tr key={s.id} className="text-white/85" data-testid={`admin-settlement-${s.id}`}>
                <td className="p-3">{s.merchant_name}</td>
                <td className="p-3 text-white/60 text-xs">{shortDate(s.period_start)} → {shortDate(s.period_end)}</td>
                <td className="p-3 text-right">{s.transaction_count}</td>
                <td className="p-3 text-right">{formatMoney(s.gross_total)}</td>
                <td className="p-3 text-right text-[#D4AF37]">{formatMoney(s.social_circle_receivable)}</td>
                <td className="p-3 text-right">
                  <select value={s.status} onChange={(e) => setStatus(s.id, e.target.value)} className="tsc-input text-xs py-1">
                    {["draft","billed","paid","overdue","disputed","waived"].map(x => <option key={x} value={x}>{x}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <p className="text-white/50 italic text-sm p-5">No settlements generated.</p>}
      </div>
    </>
  );
}

function EventsAdmin() {
  const [list, setList] = useState([]);
  const blank = { title: "", description: "", starts_at: "", ends_at: "", cover_url: "", featured: false };
  const [form, setForm] = useState(blank);
  const load = () => api.get("/events").then(({ data }) => setList(data));
  useEffect(load, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, starts_at: new Date(form.starts_at).toISOString(), ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null };
      await api.post("/events", body); setForm(blank); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Events</p>
      <h1 className="font-display text-4xl text-white mt-2">The Calendar</h1>
      <form onSubmit={submit} className="tsc-card p-5 mt-6 grid sm:grid-cols-2 gap-3">
        <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="tsc-input" placeholder="Title" />
        <input required type="datetime-local" value={form.starts_at} onChange={(e) => setForm({...form, starts_at: e.target.value})} className="tsc-input" />
        <input value={form.cover_url} onChange={(e) => setForm({...form, cover_url: e.target.value})} className="tsc-input" placeholder="Cover URL" />
        <input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({...form, ends_at: e.target.value})} className="tsc-input" />
        <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="tsc-input sm:col-span-2" rows="2" placeholder="Description" />
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} className="accent-[#D4AF37]" /><span className="text-white/70 text-sm">Featured</span></label>
        <button className="tsc-btn-gold sm:col-span-2">Add Event</button>
      </form>
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {list.map((e) => (
          <div key={e.id} className="tsc-card overflow-hidden" data-testid={`admin-event-${e.id}`}>
            {e.cover_url && <img src={e.cover_url} alt="" className="aspect-video object-cover w-full" />}
            <div className="p-4">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">{shortDate(e.starts_at)}</p>
              <p className="font-display text-lg text-white mt-1">{e.title}</p>
              <button onClick={async () => { await api.delete(`/events/${e.id}`); load(); }} className="text-white/40 text-[10px] tracking-luxe uppercase mt-2">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function PopupAdsAdmin() {
  const [list, setList] = useState([]);
  const blank = { title: "", body: "", image_url: "", cta_text: "", cta_url: "", placement: "landing", active: true };
  const [form, setForm] = useState(blank);
  const load = () => api.get("/popup-ads").then(({ data }) => setList(data));
  useEffect(load, []);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/popup-ads", form); setForm(blank); load();
  };
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">In-app campaigns</p>
      <h1 className="font-display text-4xl text-white mt-2">Pop-up Ads</h1>
      <form onSubmit={submit} className="tsc-card p-5 mt-6 grid sm:grid-cols-2 gap-3">
        <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="tsc-input" placeholder="Title" />
        <input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} className="tsc-input" placeholder="Image URL" />
        <textarea value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} rows="2" className="tsc-input sm:col-span-2" placeholder="Body" />
        <input value={form.cta_text} onChange={(e) => setForm({...form, cta_text: e.target.value})} className="tsc-input" placeholder="CTA text" />
        <input value={form.cta_url} onChange={(e) => setForm({...form, cta_url: e.target.value})} className="tsc-input" placeholder="CTA URL" />
        <select value={form.placement} onChange={(e) => setForm({...form, placement: e.target.value})} className="tsc-input">
          <option value="landing">Landing</option><option value="member">Member portal</option><option value="all">All</option>
        </select>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} className="accent-[#D4AF37]" /><span className="text-white/70 text-sm">Active</span></label>
        <button className="tsc-btn-gold sm:col-span-2">Publish Pop-up</button>
      </form>
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {list.map((a) => (
          <div key={a.id} className="tsc-card p-4">
            <p className="font-display text-lg text-white">{a.title}</p>
            <p className="text-white/55 text-xs">{a.placement} · {a.active ? "active" : "off"}</p>
            <button onClick={async () => { await api.delete(`/popup-ads/${a.id}`); load(); }} className="text-white/40 text-[10px] tracking-luxe uppercase mt-2">Delete</button>
          </div>
        ))}
      </div>
    </>
  );
}

function Analytics() {
  const [series, setSeries] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [top, setTop] = useState([]);
  useEffect(() => {
    api.get("/admin/analytics/series", { params: { days: 60 } }).then(({ data }) => setSeries(data));
    api.get("/admin/analytics/funnel").then(({ data }) => setFunnel(data));
    api.get("/admin/analytics/top-merchants").then(({ data }) => setTop(data));
  }, []);
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Analytics</p>
      <h1 className="font-display text-4xl text-white mt-2">Performance</h1>
      {funnel && (
        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <Stat label="Visit registrations" value={funnel.visit_registrations} testid="funnel-visits" />
          <Stat label="Completed guest tx" value={funnel.completed_guest_transactions} testid="funnel-completed" />
          <Stat label="Members" value={funnel.members} testid="funnel-members" />
        </div>
      )}
      <div className="tsc-card p-6 mt-8">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Daily transaction count</p>
        <div className="h-72 mt-4">
          <ResponsiveContainer><LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis dataKey="date" stroke="#666" fontSize={10} />
            <YAxis stroke="#666" fontSize={10} />
            <RTooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #333" }} />
            <Line type="monotone" dataKey="count" stroke="#D4AF37" strokeWidth={2} dot={false} />
          </LineChart></ResponsiveContainer>
        </div>
      </div>
      <div className="tsc-card p-6 mt-8">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Top merchants</p>
        <div className="mt-4 divide-y divide-white/5">
          {top.map((t, i) => (
            <div key={i} className="py-3 flex items-center justify-between">
              <div><p className="text-white">{t.merchant?.business_name || "—"}</p><p className="text-white/50 text-xs">{t.count} transactions</p></div>
              <div className="text-right"><p className="text-white">{formatMoney(t.gross)}</p><p className="text-[#D4AF37] text-xs">Margin {formatMoney(t.margin)}</p></div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Suspicious() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/suspicious").then(({ data }) => setList(data)); }, []);
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Compliance</p>
      <h1 className="font-display text-4xl text-white mt-2">Suspicious activity</h1>
      <div className="tsc-card mt-6 divide-y divide-white/5">
        {list.length === 0 ? <p className="text-white/50 italic text-sm p-5">All clear. No flags.</p> : list.map((s) => (
          <div key={s.id} className="p-4" data-testid={`admin-suspicious-${s.id}`}>
            <p className="text-amber-200 text-sm">{s.type === "rapid_multi_venue" ? "Same QR used at different venues within 1 hour" : s.type}</p>
            <p className="text-white/60 text-xs mt-1">{shortDate(s.created_at)} · Member: {s.member?.full_name} · Now at: {s.merchant?.business_name}</p>
          </div>
        ))}
      </div>
    </>
  );
}

function MembershipRequests() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [actionNote, setActionNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const load = () => api.get("/membership/requests", { params: filter ? { status: filter } : {} }).then(({ data }) => setList(data));
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    await api.post(`/membership/requests/${id}/approve`, { notes: actionNote });
    toast.success("Membership approved");
    setActionNote(""); setExpanded(null); load();
  };
  const reject = async (id) => {
    if (!rejectReason.trim()) { toast.error("Provide a rejection reason"); return; }
    await api.post(`/membership/requests/${id}/reject`, { reason: rejectReason, notes: actionNote });
    toast.success("Membership rejected");
    setRejectReason(""); setActionNote(""); setExpanded(null); load();
  };
  const resubmit = async (id) => {
    if (!rejectReason.trim()) { toast.error("Provide a reason"); return; }
    await api.post(`/membership/requests/${id}/request-resubmission`, { reason: rejectReason, notes: actionNote });
    toast.success("Resubmission requested");
    setRejectReason(""); setActionNote(""); setExpanded(null); load();
  };
  const suspend = async (id) => {
    if (!window.confirm("Suspend this member?")) return;
    await api.post(`/membership/requests/${id}/suspend`, { notes: actionNote });
    toast.success("Member suspended"); load();
  };
  const setExpiry = async (id, expiry_date) => {
    if (!expiry_date) return;
    await api.patch(`/membership/requests/${id}/expiry`, { expiry_date: new Date(expiry_date + "T23:59:59").toISOString() });
    toast.success("Expiry updated"); load();
  };

  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Membership Workflow</p>
      <h1 className="font-display text-4xl text-white mt-2">Membership Requests</h1>
      <div className="flex gap-2 mt-6 overflow-x-auto no-scrollbar pb-1">
        {[
          { v: "", l: "Pending only" },
          { v: "pending_payment", l: "Pending Payment" },
          { v: "pending_verification", l: "Pending Verification" },
          { v: "rejected", l: "Rejected" },
          { v: "active", l: "Active" },
          { v: "suspended", l: "Suspended" },
          { v: "expired", l: "Expired" },
        ].map((b) => (
          <button key={b.v} onClick={() => setFilter(b.v)}
            className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border whitespace-nowrap ${filter === b.v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70"}`}
            data-testid={`req-filter-${b.v || "pending"}`}>{b.l}</button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {list.length === 0 ? <p className="text-white/50 italic text-sm">No requests in this view.</p> : list.map((m) => (
          <div key={m.id} className="tsc-card overflow-hidden" data-testid={`request-${m.id}`}>
            <div className="p-5 flex items-center justify-between gap-4 cursor-pointer" onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
              <div>
                <p className="text-white">{m.full_name} {m.nickname && <span className="text-white/40 text-sm">"{m.nickname}"</span>}</p>
                <p className="text-white/55 text-xs">{m.email} · {m.phone}</p>
              </div>
              <div className="text-right">
                <span className={`text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full ${m.status === "active" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : m.status === "rejected" || m.status === "expired" || m.status === "suspended" ? "bg-red-500/10 text-red-300 border border-red-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>{m.status.replace(/_/g, " ")}</span>
                {m.payment_submitted_at && <p className="text-white/40 text-[10px] mt-1">submitted {shortDate(m.payment_submitted_at)}</p>}
              </div>
            </div>
            {expanded === m.id && (
              <div className="border-t border-white/5 p-5 grid lg:grid-cols-2 gap-5 bg-[#070707]">
                <div className="space-y-2 text-sm">
                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Applicant</p>
                  <Row label="City">{m.city || "—"}</Row>
                  <Row label="Birthday">{m.birthday || "—"}</Row>
                  <Row label="Gender">{m.gender || "—"}</Row>
                  <Row label="Tier">{m.tier_name}</Row>
                  <Row label="Submitted">{m.payment_submitted_at ? shortDate(m.payment_submitted_at) : "—"}</Row>
                  <Row label="Start">{m.start_date ? shortDate(m.start_date) : "—"}</Row>
                  <Row label="Expiry">
                    {m.expiry_date ? shortDate(m.expiry_date) : "—"}
                    {m.status === "active" && (
                      <input type="date" className="tsc-input ml-2 inline-block w-auto text-xs py-1" onChange={(e) => setExpiry(m.id, e.target.value)} data-testid={`req-expiry-${m.id}`} />
                    )}
                  </Row>
                  {m.rejection_reason && <Row label="Last reason">{m.rejection_reason}</Row>}
                </div>
                <div>
                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Payment screenshot</p>
                  {m.payment_screenshot_url ? (
                    <a href={m.payment_screenshot_url} target="_blank" rel="noreferrer">
                      <img src={m.payment_screenshot_url} alt="Payment proof" className="mt-2 rounded-md border border-white/10 max-h-72 object-contain bg-black" data-testid={`req-screenshot-${m.id}`} />
                    </a>
                  ) : <p className="text-white/40 text-xs mt-2 italic">No screenshot submitted yet.</p>}
                </div>

                {(m.status === "pending_verification" || m.status === "pending_payment" || m.status === "rejected") && (
                  <div className="lg:col-span-2 space-y-3 pt-3 border-t border-white/5">
                    <input value={actionNote} onChange={(e) => setActionNote(e.target.value)} className="tsc-input" placeholder="Admin notes (optional)" data-testid={`req-notes-${m.id}`} />
                    <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="tsc-input" placeholder="Reason for reject / resubmission" data-testid={`req-reason-${m.id}`} />
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => approve(m.id)} className="tsc-btn-gold" data-testid={`req-approve-${m.id}`}>Approve</button>
                      <button onClick={() => reject(m.id)} className="tsc-btn-outline" data-testid={`req-reject-${m.id}`}>Reject</button>
                      <button onClick={() => resubmit(m.id)} className="tsc-btn-outline" data-testid={`req-resubmit-${m.id}`}>Request resubmission</button>
                    </div>
                  </div>
                )}
                {m.status === "active" && (
                  <div className="lg:col-span-2 pt-3 border-t border-white/5">
                    <button onClick={() => suspend(m.id)} className="text-red-300/80 hover:text-red-300 text-[10px] tracking-luxe uppercase">Suspend member</button>
                  </div>
                )}
                {m.status === "suspended" && (
                  <div className="lg:col-span-2 pt-3 border-t border-white/5">
                    <button onClick={async () => { await api.post(`/membership/requests/${m.id}/reactivate`, {}); load(); }} className="tsc-btn-outline">Reactivate</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

const Row = ({ label, children }) => (
  <div className="flex items-baseline gap-2"><span className="text-white/40 text-[10px] tracking-luxe uppercase w-24">{label}</span><span className="text-white/80 text-sm">{children}</span></div>
);

function PaymentSettings() {
  const [s, setS] = useState(null);
  const save = async () => {
    await api.put("/settings/payment", s);
    toast.success("Payment settings saved");
  };
  useEffect(() => { api.get("/settings/payment").then(({ data }) => setS(data)); }, []);
  if (!s) return <p className="text-white/60">Loading…</p>;
  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Configuration</p>
      <h1 className="font-display text-4xl text-white mt-2">Payment & Chat Settings</h1>
      <p className="text-white/55 text-sm mt-2 max-w-xl">A single GCash QR powers regular membership, Hydro Membership, and shop orders. Configure prices, instructions, and chat below.</p>

      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <div className="tsc-card p-6 space-y-4" data-testid="payment-settings-form">
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">GCash account</p>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">Account name</label>
            <input value={s.gcash_account_name || ""} onChange={(e) => setS({ ...s, gcash_account_name: e.target.value })} className="tsc-input mt-1.5" data-testid="pay-acc-name" />
          </div>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">GCash number</label>
            <input value={s.gcash_number || ""} onChange={(e) => setS({ ...s, gcash_number: e.target.value })} className="tsc-input mt-1.5" data-testid="pay-acc-number" />
          </div>

          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37] pt-3">Membership pricing</p>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">Regular membership price (₱)</label>
            <input type="number" min="0" step="1" value={s.membership_price || 0} onChange={(e) => setS({ ...s, membership_price: Number(e.target.value) })} className="tsc-input mt-1.5" data-testid="pay-price" />
          </div>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">Membership instructions</label>
            <textarea rows="3" value={s.payment_instructions || ""} onChange={(e) => setS({ ...s, payment_instructions: e.target.value })} className="tsc-input mt-1.5" data-testid="pay-instructions" />
          </div>

          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37] pt-3">Hydro Membership</p>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">Hydro price (₱)</label>
            <input type="number" min="0" step="1" value={s.hydro_membership_price || 0} onChange={(e) => setS({ ...s, hydro_membership_price: Number(e.target.value) })} className="tsc-input mt-1.5" data-testid="pay-hydro-price" />
          </div>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">Hydro payment instructions</label>
            <textarea rows="3" value={s.hydro_payment_instructions || ""} onChange={(e) => setS({ ...s, hydro_payment_instructions: e.target.value })} className="tsc-input mt-1.5" data-testid="pay-hydro-instructions" />
          </div>

          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37] pt-3">Shop checkout</p>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">Shop payment instructions</label>
            <textarea rows="3" value={s.shop_payment_instructions || ""} onChange={(e) => setS({ ...s, shop_payment_instructions: e.target.value })} className="tsc-input mt-1.5" data-testid="pay-shop-instructions" />
          </div>

          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37] pt-3">Messenger chat</p>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">Facebook page / Messenger URL</label>
            <input value={s.facebook_messenger_url || ""} onChange={(e) => setS({ ...s, facebook_messenger_url: e.target.value })} className="tsc-input mt-1.5" placeholder="https://m.me/yourpage" data-testid="pay-fb-url" />
          </div>
          <div>
            <label className="text-[10px] tracking-luxe uppercase text-white/60">Default message (optional)</label>
            <input value={s.chat_default_message || ""} onChange={(e) => setS({ ...s, chat_default_message: e.target.value })} className="tsc-input mt-1.5" placeholder="Hi! I'd like to know more about The Social Circle." data-testid="pay-fb-msg" />
          </div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!s.chat_button_enabled} onChange={(e) => setS({ ...s, chat_button_enabled: e.target.checked })} className="accent-[#D4AF37]" data-testid="pay-chat-enabled" /><span className="text-white/70 text-sm">Show Messenger chat button on public pages</span></label>

          <label className="flex items-center gap-2 pt-2"><input type="checkbox" checked={!!s.active} onChange={(e) => setS({ ...s, active: e.target.checked })} className="accent-[#D4AF37]" data-testid="pay-active" /><span className="text-white/70 text-sm">Payment system active</span></label>
          <button onClick={save} className="tsc-btn-gold" data-testid="pay-save">Save Settings</button>
        </div>

        <div className="space-y-6">
          <div>
            <ImageUpload label="GCash QR image" value={s.gcash_qr_url} onChange={(url) => setS({ ...s, gcash_qr_url: url })} aspect="aspect-square" testId="gcash-qr-upload" />
            <p className="text-white/40 text-xs mt-2">Reused across regular membership, Hydro, and shop checkout.</p>
          </div>
          <div>
            <ImageUpload label="Hydro Membership mechanics image" value={s.hydro_mechanics_image_url} onChange={(url) => setS({ ...s, hydro_mechanics_image_url: url })} aspect="aspect-[3/4]" testId="hydro-mechanics-upload" />
            <p className="text-white/40 text-xs mt-2">Shown in the Hydro Membership modal on public pages.</p>
          </div>
        </div>
      </div>
    </>
  );
}

function ElegantQRs() {
  const origin = window.location.origin;
  const codes = [
    { id: "landing", label: "Visit The Social Circle", url: origin + "/", caption: "Landing page" },
    { id: "register", label: "Become a Member", url: origin + "/register", caption: "Member registration" },
    { id: "membership", label: "Membership", url: origin + "/membership", caption: "Membership tiers" },
    { id: "partners", label: "Partner Venues", url: origin + "/partners", caption: "Partner gallery" },
  ];
  const refs = useRef({});
  const brandLogo = useBrandLogo();

  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Marketing assets</p>
      <h1 className="font-display text-4xl text-white mt-2">Elegant QR Codes</h1>
      <p className="text-white/55 text-sm mt-2 max-w-2xl">Branded QR codes for posters, table tents, and merchant collateral. <span className="text-white/80">Download QR</span> is the primary action — saves a high-resolution PNG of the full card. Use <span className="text-white/80">Download SVG</span> for vector layout, <span className="text-white/80">Copy</span> or <span className="text-white/80">Share</span> for messaging apps, or <span className="text-white/80">Open preview</span> to save manually.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
        {codes.map((c) => (
          <div key={c.id} data-testid={`elegant-qr-${c.id}`}>
            <div ref={(el) => (refs.current[c.id] = el)} className="qr-frame p-6 rounded-md text-center" style={{ background: "#050505" }}>
              <div className="flex flex-col items-center gap-1.5">
                <img src={brandLogo} alt="" className="h-12 w-12 rounded-full ring-1 ring-[#D4AF37]/40" />
                <p className="font-display text-lg text-white tracking-wide">THE SOCIAL CIRCLE</p>
                <p className="text-[9px] tracking-luxe uppercase text-[#D4AF37]">by Cornerstone</p>
              </div>
              <p className="font-display text-xl text-white mt-4">{c.label}</p>
              <div className="bg-white p-3 rounded mx-auto inline-block mt-4">
                <QRCodeSVG id={`tsc-qr-${c.id}`} value={c.url} size={180} bgColor="#ffffff" fgColor="#0a0a0a" level="M" />
              </div>
              <p className="font-mono text-[#D4AF37] text-[10px] tracking-wider mt-3 break-all">{c.url}</p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <div className="h-px w-8 bg-[#D4AF37]/40" /><div className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" /><div className="h-px w-8 bg-[#D4AF37]/40" />
              </div>
              <p className="text-[9px] tracking-luxe uppercase text-[#D4AF37]/80 mt-2">{c.caption}</p>
            </div>
            <div className="mt-4">
              <CardActions
                targetRef={{ current: refs.current[c.id] }}
                svgElementId={`tsc-qr-${c.id}`}
                filenameBase={`social-circle-${c.id}-qr`}
                shareTitle={c.label}
                shareText="The Social Circle by Cornerstone"
                shareUrl={c.url}
                layout="grid"
                testIdPrefix={`qr-actions-${c.id}`}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// Hydro Applications · Shop Products · Shop Orders · Merchant Apps
// ------------------------------------------------------------------

function HydroApplicationsAdmin() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [actionNote, setActionNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const load = () => api.get("/hydro/applications", { params: filter ? { status: filter } : {} }).then(({ data }) => setList(data));
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    await api.post(`/hydro/applications/${id}/approve`, { notes: actionNote });
    toast.success("Hydro membership approved");
    setActionNote(""); setExpanded(null); load();
  };
  const reject = async (id) => {
    if (!rejectReason.trim()) { toast.error("Provide a rejection reason"); return; }
    await api.post(`/hydro/applications/${id}/reject`, { reason: rejectReason, notes: actionNote });
    toast.success("Hydro application rejected");
    setRejectReason(""); setActionNote(""); setExpanded(null); load();
  };
  const suspend = async (id) => {
    if (!window.confirm("Suspend this Hydro member?")) return;
    await api.post(`/hydro/applications/${id}/suspend`, { notes: actionNote });
    toast.success("Hydro member suspended"); load();
  };
  const reactivate = async (id) => {
    await api.post(`/hydro/applications/${id}/reactivate`, {});
    toast.success("Reactivated"); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this Hydro application?")) return;
    await api.delete(`/hydro/applications/${id}`); load();
  };

  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Hydro Membership</p>
      <h1 className="font-display text-4xl text-white mt-2">Hydro Applications</h1>
      <div className="flex gap-2 mt-6 overflow-x-auto no-scrollbar pb-1">
        {[
          { v: "", l: "All" },
          { v: "pending_payment", l: "Pending Payment" },
          { v: "pending_verification", l: "Pending Verification" },
          { v: "active", l: "Active" },
          { v: "rejected", l: "Rejected" },
          { v: "suspended", l: "Suspended" },
          { v: "expired", l: "Expired" },
        ].map((b) => (
          <button key={b.v} onClick={() => setFilter(b.v)} data-testid={`hydro-filter-${b.v || "all"}`}
            className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border whitespace-nowrap ${filter === b.v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70"}`}>{b.l}</button>
        ))}
      </div>
      <div className="mt-6 space-y-3">
        {list.length === 0 ? <p className="text-white/50 italic text-sm">No Hydro applications.</p> : list.map((m) => (
          <div key={m.id} className="tsc-card overflow-hidden" data-testid={`hydro-app-${m.id}`}>
            <div className="p-5 flex items-center justify-between gap-4 cursor-pointer" onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
              <div>
                <p className="text-white">{m.full_name}</p>
                <p className="text-white/55 text-xs">{m.email} · {m.phone}</p>
              </div>
              <div className="text-right">
                <span className={`text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full ${m.status === "active" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : m.status === "rejected" || m.status === "expired" || m.status === "suspended" ? "bg-red-500/10 text-red-300 border border-red-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>{m.status.replace(/_/g, " ")}</span>
                {m.payment_submitted_at && <p className="text-white/40 text-[10px] mt-1">submitted {shortDate(m.payment_submitted_at)}</p>}
              </div>
            </div>
            {expanded === m.id && (
              <div className="border-t border-white/5 p-5 grid lg:grid-cols-2 gap-5 bg-[#070707]">
                <div className="space-y-2 text-sm">
                  <Row label="City">{m.city || "—"}</Row>
                  <Row label="Created">{shortDate(m.created_at)}</Row>
                  <Row label="Start">{m.start_date ? shortDate(m.start_date) : "—"}</Row>
                  <Row label="Expiry">{m.expiry_date ? shortDate(m.expiry_date) : "—"}</Row>
                  {m.rejection_reason && <Row label="Last reason">{m.rejection_reason}</Row>}
                  {m.admin_notes && <Row label="Notes">{m.admin_notes}</Row>}
                </div>
                <div>
                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Payment screenshot</p>
                  {m.payment_screenshot_url ? (
                    <a href={m.payment_screenshot_url} target="_blank" rel="noreferrer">
                      <img src={m.payment_screenshot_url} alt="Payment proof" className="mt-2 rounded-md border border-white/10 max-h-72 object-contain bg-black" data-testid={`hydro-screenshot-${m.id}`} />
                    </a>
                  ) : <p className="text-white/40 text-xs mt-2 italic">No screenshot submitted yet.</p>}
                </div>
                {(m.status === "pending_verification" || m.status === "rejected") && (
                  <div className="lg:col-span-2 space-y-3 pt-3 border-t border-white/5">
                    <input value={actionNote} onChange={(e) => setActionNote(e.target.value)} className="tsc-input" placeholder="Admin notes (optional)" data-testid={`hydro-notes-${m.id}`} />
                    <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="tsc-input" placeholder="Reason for reject" data-testid={`hydro-reason-${m.id}`} />
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => approve(m.id)} className="tsc-btn-gold" data-testid={`hydro-approve-${m.id}`}>Approve</button>
                      <button onClick={() => reject(m.id)} className="tsc-btn-outline" data-testid={`hydro-reject-${m.id}`}>Reject</button>
                    </div>
                  </div>
                )}
                {m.status === "active" && (
                  <div className="lg:col-span-2 pt-3 border-t border-white/5">
                    <button onClick={() => suspend(m.id)} className="text-red-300/80 hover:text-red-300 text-[10px] tracking-luxe uppercase" data-testid={`hydro-suspend-${m.id}`}>Suspend</button>
                  </div>
                )}
                {m.status === "suspended" && (
                  <div className="lg:col-span-2 pt-3 border-t border-white/5">
                    <button onClick={() => reactivate(m.id)} className="tsc-btn-outline" data-testid={`hydro-reactivate-${m.id}`}>Reactivate</button>
                  </div>
                )}
                <div className="lg:col-span-2 pt-3 border-t border-white/5 text-right">
                  <button onClick={() => del(m.id)} className="text-white/40 hover:text-red-400 text-[10px] tracking-luxe uppercase" data-testid={`hydro-delete-${m.id}`}>Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function ShopProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [newCat, setNewCat] = useState("");
  const blank = { title: "", short_description: "", description: "", category_id: "", price: 0, delivery_fee: 0, featured_photo: "", gallery: [], stock: "", status: "active", featured: false };
  const [form, setForm] = useState(blank);

  const load = () => api.get("/shop/products", { params: { status: "" } }).then(({ data }) => setProducts(data));
  const loadCats = () => api.get("/shop/categories").then(({ data }) => setCats(data));
  useEffect(() => { load(); loadCats(); }, []);

  const openCreate = () => { setEditId(null); setForm(blank); setOpen(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ ...blank, ...p, stock: p.stock ?? "", featured: !!p.featured, gallery: p.gallery || [] });
    setOpen(true); window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const closeForm = () => { setOpen(false); setEditId(null); setForm(blank); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    payload.price = Number(payload.price) || 0;
    payload.delivery_fee = Number(payload.delivery_fee) || 0;
    payload.stock = payload.stock === "" ? null : Number(payload.stock);
    payload.category_id = payload.category_id || null;
    try {
      if (editId) await api.patch(`/shop/products/${editId}`, payload);
      else await api.post("/shop/products", payload);
      toast.success(editId ? "Product saved" : "Product created");
      closeForm(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete product "${p.title}"?`)) return;
    await api.delete(`/shop/products/${p.id}`); load();
  };

  const addCat = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try { await api.post("/shop/categories", { name: newCat.trim(), sort_order: cats.length }); setNewCat(""); loadCats(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const delCat = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    await api.delete(`/shop/categories/${c.id}`); loadCats(); load();
  };

  return (
    <>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Shop</p>
          <h1 className="font-display text-4xl text-white mt-2">Products</h1>
        </div>
        {open ? (
          <button onClick={closeForm} className="tsc-btn-outline" data-testid="shop-cancel">Cancel</button>
        ) : (
          <button onClick={openCreate} className="tsc-btn-gold" data-testid="shop-add-product">+ New Product</button>
        )}
      </div>

      <div className="tsc-card p-5 mt-6">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Shop categories</p>
        <form onSubmit={addCat} className="mt-3 flex gap-2 flex-wrap">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} className="tsc-input flex-1 min-w-[200px]" placeholder="Add category…" data-testid="shop-cat-input" />
          <button className="tsc-btn-outline" data-testid="shop-cat-add">Add</button>
        </form>
        <div className="flex flex-wrap gap-2 mt-3">
          {cats.map((c) => (
            <span key={c.id} className="text-[10px] tracking-luxe uppercase border border-white/10 rounded-full px-3 py-1 text-white/70 flex items-center gap-2" data-testid={`shop-cat-pill-${c.id}`}>
              {c.name} <button onClick={() => delCat(c)} className="text-white/30 hover:text-red-400">×</button>
            </span>
          ))}
          {cats.length === 0 && <span className="text-white/40 text-xs italic">No categories yet.</span>}
        </div>
      </div>

      {open && (
        <form onSubmit={submit} className="tsc-card p-6 mt-6 grid sm:grid-cols-2 gap-4" data-testid="shop-product-form">
          <div className="sm:col-span-2 flex items-center justify-between">
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">{editId ? "Edit product" : "New product"}</p>
          </div>
          <Field label="Title"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="tsc-input" data-testid="prod-form-title" /></Field>
          <Field label="Category">
            <select value={form.category_id || ""} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="tsc-input" data-testid="prod-form-category">
              <option value="">No category</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Short description" full><input value={form.short_description || ""} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="tsc-input" placeholder="One-liner shown on product cards" data-testid="prod-form-short" /></Field>
          <Field label="Description" full><textarea rows="4" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="tsc-input" data-testid="prod-form-desc" /></Field>
          <Field label="Price (₱)"><input required type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="tsc-input" data-testid="prod-form-price" /></Field>
          <Field label="Delivery fee (₱)"><input type="number" min="0" step="0.01" value={form.delivery_fee} onChange={(e) => setForm({ ...form, delivery_fee: e.target.value })} className="tsc-input" data-testid="prod-form-delivery" /></Field>
          <Field label="Stock (optional)"><input type="number" min="0" step="1" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="tsc-input" placeholder="Leave empty for unlimited" data-testid="prod-form-stock" /></Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="tsc-input" data-testid="prod-form-status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </Field>
          <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4">
            <div>
              <ImageUpload label="Featured photo" value={form.featured_photo} onChange={(url) => setForm({ ...form, featured_photo: url })} aspect="aspect-[4/3]" testId="prod-form-photo" />
            </div>
            <div className="sm:col-span-2">
              <GalleryUpload value={form.gallery} onChange={(arr) => setForm({ ...form, gallery: arr })} testId="prod-form-gallery" />
            </div>
          </div>
          <Field label="Featured?"><label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#D4AF37]" data-testid="prod-form-featured" /><span className="text-white/70 text-sm">Show on Shop featured section</span></label></Field>
          <div className="sm:col-span-2">
            <button type="submit" className="tsc-btn-gold" data-testid="prod-form-submit">{editId ? "Save changes" : "Create product"}</button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {products.length === 0 && <p className="text-white/50 italic text-sm">No products yet.</p>}
        {products.map((p) => (
          <div key={p.id} className="tsc-card overflow-hidden" data-testid={`shop-admin-product-${p.id}`}>
            <div className="aspect-[4/3] bg-[#0a0a0a]">
              {p.featured_photo ? <img src={p.featured_photo} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="p-4">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">{p.category?.name || "—"} {p.featured && <span className="ml-1 text-[#F9E596]">· Featured</span>}</p>
              <p className="font-display text-lg text-white mt-1">{p.title}</p>
              <div className="flex items-center justify-between mt-2">
                <p className="text-white">{formatMoney(p.price)}</p>
                <p className="text-white/50 text-xs">{p.status} {p.stock !== null && p.stock !== undefined ? `· ${p.stock} stock` : ""}</p>
              </div>
              <div className="flex justify-end gap-3 mt-3">
                <button onClick={() => openEdit(p)} className="text-[#D4AF37] hover:text-[#F9E596] text-[10px] tracking-luxe uppercase" data-testid={`shop-admin-edit-${p.id}`}>Edit</button>
                <button onClick={() => del(p)} className="text-white/40 hover:text-red-400 text-[10px] tracking-luxe uppercase" data-testid={`shop-admin-delete-${p.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function ShopOrdersAdmin() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [statusUpd, setStatusUpd] = useState({});

  const load = () => api.get("/orders", { params: filter ? { status: filter } : {} }).then(({ data }) => setList(data));
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id) => {
    const { status, notes } = statusUpd[id] || {};
    if (!status) { toast.error("Pick a new status"); return; }
    try {
      await api.patch(`/orders/${id}/status`, { status, notes: notes || null });
      toast.success(`Order moved to ${status}`);
      setStatusUpd({ ...statusUpd, [id]: {} });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Shop</p>
      <h1 className="font-display text-4xl text-white mt-2">Orders</h1>
      <div className="flex gap-2 mt-6 overflow-x-auto no-scrollbar pb-1">
        {[
          { v: "", l: "All" },
          { v: "pending_payment", l: "Pending Payment" },
          { v: "pending_verification", l: "Pending Verification" },
          { v: "paid", l: "Paid" },
          { v: "processing", l: "Processing" },
          { v: "shipped", l: "Shipped" },
          { v: "completed", l: "Completed" },
          { v: "rejected", l: "Rejected" },
          { v: "cancelled", l: "Cancelled" },
        ].map((b) => (
          <button key={b.v} onClick={() => setFilter(b.v)} data-testid={`order-filter-${b.v || "all"}`}
            className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border whitespace-nowrap ${filter === b.v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70"}`}>{b.l}</button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {list.length === 0 ? <p className="text-white/50 italic text-sm">No orders.</p> : list.map((o) => (
          <div key={o.id} className="tsc-card overflow-hidden" data-testid={`order-${o.id}`}>
            <div className="p-5 flex items-center justify-between gap-4 cursor-pointer" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
              <div>
                <p className="text-white"><span className="font-mono text-[#D4AF37] tracking-[0.18em]">{o.ref_code}</span> · {o.customer_email || "guest"}</p>
                <p className="text-white/45 text-xs">{shortDate(o.created_at)} · {o.items.length} items · {formatMoney(o.total)}</p>
              </div>
              <span className={`text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full ${o.status === "completed" || o.status === "paid" || o.status === "shipped" || o.status === "processing" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : o.status === "rejected" || o.status === "cancelled" ? "bg-red-500/10 text-red-300 border border-red-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>{o.status.replace(/_/g, " ")}</span>
            </div>
            {expanded === o.id && (
              <div className="border-t border-white/5 p-5 grid lg:grid-cols-2 gap-5 bg-[#070707]">
                <div>
                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Items</p>
                  <div className="mt-2 divide-y divide-white/5 text-sm">
                    {o.items.map((it, i) => (
                      <div key={i} className="py-2 flex items-center gap-3">
                        {it.featured_photo && <img src={it.featured_photo} alt="" className="w-12 h-12 rounded object-cover" />}
                        <div className="flex-1">
                          <p className="text-white">{it.title}</p>
                          <p className="text-white/40 text-xs">{formatMoney(it.unit_price)} × {it.quantity}</p>
                        </div>
                        <p className="text-white">{formatMoney(it.line_total)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5 text-sm space-y-1">
                    <Row label="Subtotal">{formatMoney(o.subtotal)}</Row>
                    <Row label="Delivery">{formatMoney(o.delivery_fee)}</Row>
                    <Row label="Total"><span className="text-[#D4AF37] font-medium">{formatMoney(o.total)}</span></Row>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Delivery</p>
                  {o.delivery_address ? (
                    <div className="text-sm space-y-0.5 mt-2 text-white/80">
                      <p>{o.delivery_address.full_name}</p>
                      <p className="text-white/55">{o.delivery_address.phone}</p>
                      <p className="text-white/55">{o.delivery_address.line1}{o.delivery_address.line2 ? `, ${o.delivery_address.line2}` : ""}</p>
                      <p className="text-white/55">{o.delivery_address.city}{o.delivery_address.province ? `, ${o.delivery_address.province}` : ""}{o.delivery_address.postal_code ? ` ${o.delivery_address.postal_code}` : ""}</p>
                      {o.delivery_address.notes && <p className="text-white/45 italic mt-1">Notes: {o.delivery_address.notes}</p>}
                    </div>
                  ) : <p className="text-white/40 italic text-xs mt-2">No address.</p>}

                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37] mt-5">Payment screenshot</p>
                  {o.payment_screenshot_url ? (
                    <a href={o.payment_screenshot_url} target="_blank" rel="noreferrer">
                      <img src={o.payment_screenshot_url} alt="Payment proof" className="mt-2 rounded-md border border-white/10 max-h-72 object-contain bg-black" data-testid={`order-screenshot-${o.id}`} />
                    </a>
                  ) : <p className="text-white/40 text-xs mt-2 italic">No screenshot uploaded yet.</p>}
                </div>

                <div className="lg:col-span-2 space-y-3 pt-3 border-t border-white/5">
                  <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Update status</p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <select value={statusUpd[o.id]?.status || ""} onChange={(e) => setStatusUpd({ ...statusUpd, [o.id]: { ...(statusUpd[o.id] || {}), status: e.target.value } })} className="tsc-input max-w-xs" data-testid={`order-status-select-${o.id}`}>
                      <option value="">— pick status —</option>
                      {["pending_payment", "pending_verification", "paid", "processing", "shipped", "completed", "rejected", "cancelled"].map((st) => <option key={st} value={st}>{st.replace(/_/g, " ")}</option>)}
                    </select>
                    <input value={statusUpd[o.id]?.notes || ""} onChange={(e) => setStatusUpd({ ...statusUpd, [o.id]: { ...(statusUpd[o.id] || {}), notes: e.target.value } })} className="tsc-input flex-1 min-w-[200px]" placeholder="Admin notes (optional)" data-testid={`order-notes-${o.id}`} />
                    <button onClick={() => updateStatus(o.id)} className="tsc-btn-gold" data-testid={`order-update-${o.id}`}>Update</button>
                  </div>
                  {o.admin_notes && <p className="text-white/45 text-xs italic">Last notes: {o.admin_notes}</p>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function MerchantApplicationsAdmin() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [convertForm, setConvertForm] = useState({});
  const [reason, setReason] = useState("");

  const load = () => api.get("/merchant-applications", { params: filter ? { status: filter } : {} }).then(({ data }) => setList(data));
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCats(data)); }, [filter]);

  const approve = async (id) => { await api.post(`/merchant-applications/${id}/approve`, {}); toast.success("Application approved"); load(); };
  const reject = async (id) => {
    if (!reason.trim()) { toast.error("Provide a reason"); return; }
    await api.post(`/merchant-applications/${id}/reject`, { reason });
    toast.success("Application rejected"); setReason(""); load();
  };
  const needsInfo = async (id) => {
    if (!reason.trim()) { toast.error("Tell the applicant what info is needed"); return; }
    await api.post(`/merchant-applications/${id}/needs-more-info`, { reason });
    toast.success("Marked needs more info"); setReason(""); load();
  };
  const del = async (id) => { if (!window.confirm("Delete this application?")) return; await api.delete(`/merchant-applications/${id}`); load(); };

  const convert = async (id) => {
    const f = convertForm[id] || {};
    if (!f.category_id) { toast.error("Pick a category"); return; }
    try {
      const { data } = await api.post(`/merchant-applications/${id}/convert`, {
        category_id: f.category_id,
        merchant_allocation_type: f.merchant_allocation_type || "percentage",
        merchant_allocation_value: Number(f.merchant_allocation_value) || 30,
        member_discount_type: f.member_discount_type || "percentage",
        member_discount_value: Number(f.member_discount_value) || 15,
        owner_email: f.owner_email || null,
        owner_password: f.owner_password || null,
        owner_name: f.owner_name || null,
      });
      toast.success(`Converted → merchant ${data.merchant.business_name}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setC = (id, patch) => setConvertForm({ ...convertForm, [id]: { ...(convertForm[id] || {}), ...patch } });

  return (
    <>
      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Partners pipeline</p>
      <h1 className="font-display text-4xl text-white mt-2">Merchant Applications</h1>
      <div className="flex gap-2 mt-6 overflow-x-auto no-scrollbar pb-1">
        {[
          { v: "", l: "All" },
          { v: "pending_review", l: "Pending Review" },
          { v: "needs_more_info", l: "Needs Info" },
          { v: "approved", l: "Approved" },
          { v: "rejected", l: "Rejected" },
        ].map((b) => (
          <button key={b.v} onClick={() => setFilter(b.v)} data-testid={`merchant-app-filter-${b.v || "all"}`}
            className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border whitespace-nowrap ${filter === b.v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70"}`}>{b.l}</button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {list.length === 0 ? <p className="text-white/50 italic text-sm">No merchant applications.</p> : list.map((m) => {
          const f = convertForm[m.id] || {};
          return (
            <div key={m.id} className="tsc-card overflow-hidden" data-testid={`merchant-app-${m.id}`}>
              <div className="p-5 flex items-center justify-between gap-4 cursor-pointer" onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
                <div>
                  <p className="text-white">{m.business_name}{m.branch_name && <span className="text-white/40"> · {m.branch_name}</span>}</p>
                  <p className="text-white/55 text-xs">{m.contact_name || "—"} · {m.email || "—"} · {m.phone || "—"}</p>
                </div>
                <span className={`text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full ${m.status === "approved" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : m.status === "rejected" ? "bg-red-500/10 text-red-300 border border-red-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>{m.status.replace(/_/g, " ")}</span>
              </div>
              {expanded === m.id && (
                <div className="border-t border-white/5 p-5 space-y-4 bg-[#070707] text-sm">
                  <Row label="Submitted">{shortDate(m.created_at)}</Row>
                  <Row label="Address">{m.address || "—"}</Row>
                  <Row label="Description">{m.description || "—"}</Row>
                  {m.rejection_reason && <Row label="Last reason">{m.rejection_reason}</Row>}
                  {m.admin_notes && <Row label="Notes">{m.admin_notes}</Row>}
                  {m.converted_merchant_id && <Row label="Converted">Merchant id {m.converted_merchant_id.slice(0, 8)}</Row>}

                  {(m.status === "pending_review" || m.status === "needs_more_info") && (
                    <div className="space-y-2 pt-3 border-t border-white/5">
                      <input value={reason} onChange={(e) => setReason(e.target.value)} className="tsc-input" placeholder="Reason / requested info" data-testid={`merchant-app-reason-${m.id}`} />
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => approve(m.id)} className="tsc-btn-gold" data-testid={`merchant-app-approve-${m.id}`}>Approve</button>
                        <button onClick={() => needsInfo(m.id)} className="tsc-btn-outline" data-testid={`merchant-app-needsinfo-${m.id}`}>Needs more info</button>
                        <button onClick={() => reject(m.id)} className="tsc-btn-outline" data-testid={`merchant-app-reject-${m.id}`}>Reject</button>
                      </div>
                    </div>
                  )}

                  {m.status === "approved" && !m.converted_merchant_id && (
                    <div className="space-y-3 pt-3 border-t border-white/5">
                      <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Convert to active merchant</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <select value={f.category_id || m.category_id || ""} onChange={(e) => setC(m.id, { category_id: e.target.value })} className="tsc-input" data-testid={`merchant-app-cat-${m.id}`}>
                          <option value="">Category…</option>
                          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <input value={f.merchant_allocation_value ?? 30} onChange={(e) => setC(m.id, { merchant_allocation_value: e.target.value })} type="number" min="0" className="tsc-input" placeholder="Merchant allocation %" />
                        <input value={f.member_discount_value ?? 15} onChange={(e) => setC(m.id, { member_discount_value: e.target.value })} type="number" min="0" className="tsc-input" placeholder="Member discount %" />
                        <div />
                        <input value={f.owner_email || ""} onChange={(e) => setC(m.id, { owner_email: e.target.value })} type="email" className="tsc-input" placeholder="Owner email (optional)" />
                        <input value={f.owner_password || ""} onChange={(e) => setC(m.id, { owner_password: e.target.value })} type="password" className="tsc-input" placeholder="Owner password (min 6)" />
                        <input value={f.owner_name || ""} onChange={(e) => setC(m.id, { owner_name: e.target.value })} className="tsc-input sm:col-span-2" placeholder="Owner name (optional)" />
                      </div>
                      <button onClick={() => convert(m.id)} className="tsc-btn-gold" data-testid={`merchant-app-convert-${m.id}`}>Convert to Active Merchant</button>
                    </div>
                  )}

                  <div className="pt-3 border-t border-white/5 text-right">
                    <button onClick={() => del(m.id)} className="text-white/40 hover:text-red-400 text-[10px] tracking-luxe uppercase" data-testid={`merchant-app-delete-${m.id}`}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}


export default function AdminDashboard() {
  const { logout, user } = useAuth();
  return (
    <div className="min-h-screen bg-[#050505] flex flex-col lg:flex-row">
      <Sidebar logout={logout} name={user?.name} />
      <main className="flex-1 p-6 sm:p-10 max-w-7xl">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="merchants" element={<MerchantsAdmin />} />
          <Route path="merchant-applications" element={<MerchantApplicationsAdmin />} />
          <Route path="membership-requests" element={<MembershipRequests />} />
          <Route path="hydro-applications" element={<HydroApplicationsAdmin />} />
          <Route path="shop" element={<ShopProductsAdmin />} />
          <Route path="orders" element={<ShopOrdersAdmin />} />
          <Route path="payment-settings" element={<PaymentSettings />} />
          <Route path="qr-codes" element={<ElegantQRs />} />
          <Route path="categories" element={<CategoriesAdmin />} />
          <Route path="members" element={<MembersAdmin />} />
          <Route path="transactions" element={<TransactionsAdmin />} />
          <Route path="settlements" element={<SettlementsAdmin />} />
          <Route path="events" element={<EventsAdmin />} />
          <Route path="popup-ads" element={<PopupAdsAdmin />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="suspicious" element={<Suspicious />} />
        </Routes>
      </main>
    </div>
  );
}
